
import React, { useEffect, useState } from "react";
import {
  Page,
  Card,
  Text,
  Box,
  InlineStack,
  Button,
  ProgressBar,
  RadioButton,
  TextField,
  Modal,
  FormLayout,
  Checkbox,
  ButtonGroup,
} from "@shopify/polaris";
import { ContextualSaveBar } from "@shopify/polaris";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import "../css/ToneContent.css";
import SetupTabs from "./SetupTabs";
import { useContent } from "../Provider/ContentContext";

function ToneContent({ shopOrigin, apiKey }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { contentData, setContentData } = useContent();

  const [step, setStep] = useState(1);
  const [stepModal, setStepModal] = useState(1);
  const [errors, setErrors] = useState({});
  const [progress, setProgress] = useState(33);
  const [showToneOptions, setShowToneOptions] = useState(false);
  const [showSampleText, setShowSampleText] = useState(false);
  const [showUrlText, setShowUrlText] = useState(false);
  const [urlText, setUrlText] = useState("");
  const [showInfo, setShowInfo] = useState(false);
  const [sampleText, setSampleText] = useState("");
  const [selectedTone, setSelectedTone] = useState("");
  const [error, setError] = useState("");
  const [showAiSetupModal, setShowAiSetupModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);


  const [mobileViewHeight, setMobileViewHeight] = useState(0);

  useEffect(() => {
    const updateHeight = () => {
      if (window.innerWidth <= 768) {
        setMobileViewHeight(window.innerHeight);
      }
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
    window.addEventListener('orientationchange', updateHeight);

    return () => {
      window.removeEventListener('resize', updateHeight);
      window.removeEventListener('orientationchange', updateHeight);
    };
  }, []);

  // Then apply dynamic styles to your tone-radio div
  const mobileStyles = window.innerWidth <= 768 ? {
    position: 'sticky',
    bottom: 0,
    left: 0,
    width: '100%',
    zIndex: 1000,
    backgroundColor: '#F1F1F1',
    // boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
    padding: '15px 12px',
    margin: 0
  } : {};

  // Initialize formData with default values
  const [formData, setFormData] = useState({
    shopUrl: "",
    noContent: false,
    competitorUrl: "",
    keywords: "",
    ageRanges: [],
    gender: "All Genders",
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [initialFormData, setInitialFormData] = useState(formData);

  // Fetch saved setup data from the server when the modal opens
  useEffect(() => {
    const fetchSetupData = async () => {
      if (showAiSetupModal && shopOrigin) {
        try {
          const response = await axios.get("/api/check-setup-status/", {
            params: { shop: shopOrigin },
          });

          if (response.data.initial_steps_completed && response.data.prefill) {
            const prefill = response.data.prefill;
            setFormData({
              shopUrl: prefill.shop_url || "",
              noContent: prefill.no_website_content || false,
              competitorUrl: prefill.competitor_url || "",
              keywords: prefill.keywords || "",
              ageRanges: prefill.age_ranges
                ? prefill.age_ranges.split(", ").filter((range) => range)
                : [],
              gender: prefill.gender || "All Genders",
            });
          }
        } catch (err) {
          console.error(
            "Error fetching setup data:",
            err.response?.data?.message || err.message
          );
          setErrors({
            general: "Failed to load saved setup data. Please try again.",
          });
        }
      }
    };

    fetchSetupData();
  }, [showAiSetupModal, shopOrigin]);

  // Check for modal query parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const modalParam = searchParams.get("modal");
    if (modalParam === "ai-setup") {
      setShowAiSetupModal(true);
      setStepModal(1); // Reset to step 1 when opening the modal
    } else {
      setShowAiSetupModal(false);
    }
  }, [location.search]);

  useEffect(() => {
    setProgress(step === 1 ? 33 : step === 2 ? 66 : 100);
    setContentData((prev) => ({ ...prev, shop: shopOrigin || "" }));
  }, [step, shopOrigin, setContentData]);

  const toneSelection = () => {
    setShowToneOptions(true);
  };

  const sampleTextSelection = () => {
    setShowSampleText(true);
  };

  const handleUrlSubmit = async () => {
    if (urlText.trim().length > 0) {
      const urlRegex = /^(https:\/\/)[^\s/$.?#].[^\s]*$/i;

      if (!urlRegex.test(urlText)) {
        setError("Please enter a valid HTTPS URL.");
        return;
      }

      const payload = {
        blog_url: urlText,
      };

      try {
        const response = await axios.post(
          `/api/analyze-tone-from-url/${shopOrigin}/`,
          payload,
          {
            headers: { "Content-Type": "application/json" },
          }
        );

        if (response.data.status === "success") {
          const detectedTone = response.data.tone;
          const extractedText = response.data.sample_text;
          // console.log("detectedTone: ", detectedTone);

          setContentData((prev) => ({
            ...prev,
            sample_text: extractedText,
            tone: detectedTone,
          }));

          // Save tone and sample text
          await axios.post(
            "/api/save-tone-content/",
            {
              shop: shopOrigin || "",
              sample_text: extractedText,
              tone: detectedTone,
            },
            { headers: { "Content-Type": "application/json" } }
          );

          setStep(2);
          navigate(
            `/content-setup?shop=${encodeURIComponent(shopOrigin || "")}`
          );
        } else {
          setError(response.data.message || "Failed to analyze URL tone.");
        }
      } catch (err) {
        setError(err.response?.data?.message || "Error analyzing URL.");
      }
    } else {
      setError("Please paste a URL.");
    }
  };

  const handleSampleSubmit = async () => {
    if (sampleText.trim().length > 0) {
      const payload = {
        sampleText: sampleText,
      };
      try {
        const response = await axios.post(
          `/api/analyze-tone/${shopOrigin}/`,
          payload,
          {
            headers: { "Content-Type": "application/json" },
          }
        );

        if (response.data.status === "success") {
          const detectedTone = response.data.tone;
          setContentData((prev) => ({
            ...prev,
            sample_text: sampleText,
            tone: detectedTone,
          }));

          // Save the tone and sample text to the existing endpoint
          const savePayload = {
            shop: shopOrigin || "",
            sample_text: sampleText,
            tone: detectedTone,
          };
          await axios.post("/api/save-tone-content/", savePayload, {
            headers: { "Content-Type": "application/json" },
          });

          setStep(2);
          navigate(
            `/content-setup?shop=${encodeURIComponent(shopOrigin || "")}`
          );
        } else {
          setError(response.data.message || "Failed to analyze tone.");
          //console.error("Tone analysis failed:", response.data.message);
        }
      } catch (err) {
        setError(err.response?.data?.message || "Error analyzing tone.");
        //console.error("Error analyzing or saving sample text:", err.response?.data || err.message);
      }
    } else {
      setError("Sample text is required.");
    }
  };

  const handleContinue = async () => {
    if (selectedTone === "tone") {
      // setShowToneOptions(true);
      setShowInfo(true);
      // setShowUrlText(false);
      // setShowSampleText(false);
      // setUrlText("");
      // setSampleText("");
    } else if (selectedTone === "url") {
      // setShowUrlText(true);
      setShowInfo(true);
      // setShowToneOptions(false);
      // setShowSampleText(false);
      // setSampleText("");
      // setSelectedTone("");
    } else if (selectedTone === "sample") {
      // setShowSampleText(true);
      setShowInfo(true);
      // setShowToneOptions(false);
      // setShowUrlText(false);
      // setUrlText("");
      // setSelectedTone("");
    }

    if (selectedTone) {
      setContentData((prev) => ({ ...prev, tone: selectedTone }));
      const payload = {
        shop: shopOrigin || "",
        tone: selectedTone,
      };
      // console.log("Payload:", payload);
      try {
        await axios.post("/api/save-tone-content/", payload, {
          headers: { "Content-Type": "application/json" },
        });
        //console.log("Tone saved:", response.data);
        setStep(2);
        navigate(`/content-setup?shop=${encodeURIComponent(shopOrigin || "")}`);
      } catch (err) {
        setError(err.response?.data?.message || "Error saving tone.");
        //console.error("Error saving tone:", err.response?.data || err.message);
      }
    } else {
      setError("Please select a tone.");
    }
  };

  useEffect(() => {
    if (showToneOptions && contentData?.tone) {
      setSelectedTone(contentData.tone);
    }
  }, [showToneOptions, contentData]);

  // Handle modal close

  const handleModalClose = () => {
    // const requiredStep1Complete =
    //   formData.noContent || formData.shopUrl.trim().length > 0;
    // const requiredStep2Complete =
    //   formData.competitorUrl.trim().length > 0 &&
    //   formData.keywords.trim().length > 0 &&
    //   formData.ageRanges.length > 0 &&
    //   formData.gender;
    // if (stepModal === 1 && !requiredStep1Complete) {
    //   setErrors((prev) => ({
    //     ...prev,
    //     general: "Please complete Step 1 before continuing.",
    //   }));
    //   return;
    // }
    // if (stepModal === 1 && requiredStep1Complete) {
    //   // Move to Step 2 instead of closing
    //   setStepModal(2);
    //   setErrors({});
    //   return;
    // }
    // if (stepModal === 2 && !requiredStep2Complete) {
    //   setErrors((prev) => ({
    //     ...prev,
    //     general: "Please complete Step 2 before closing.",
    //   }));
    //   return;
    // }
    // // All steps complete: close modal
    // setShowAiSetupModal(false);
    // setErrors({});
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.shopUrl && !formData.noContent) {
      newErrors.shopUrl = "This field is required or tick the checkbox.";
      setErrors(newErrors);
      return false;
    }
    return validateSingleField("shopUrl", formData.shopUrl);
  };

  const toneOptions = [
    {
      label: "Friendly",
      value: "friendly",
      description: "Warm approachable content that builds connection",
    },
    {
      label: "Serious",
      value: "serious",
      description: "Professional authoritative content",
    },
    {
      label: "Casual",
      value: "casual",
      description: "Relaxed conversational writing style",
    },
    {
      label: "Scientific",
      value: "scientific",
      description: "Data-driven research-based content",
    },
    {
      label: "Bubbly",
      value: "bubbly",
      description: "Energetic, fun and engaging content",
    },
    {
      label: "Inspirational",
      value: "inspirational",
      description: "Motivational uplifting writing style",
    },
    {
      label: "Professional",
      value: "professional",
      description: "Clear, concise and polished content suitable for business contexts",
    },
    {
      label: "Humorous",
      value: "humorous",
      description: "Light-hearted, witty and entertaining writing style",
    },
    {
      label: "Persuasive",
      value: "persuasive",
      description: "Compelling content designed to influence or convince the reader",
    },
    {
      label: "Empathetic",
      value: "empathetic",
      description: "Compassionate, understanding and supportive writing style",
    },
    {
      label: "Neutral",
      value: "neutral",
      description: "Balanced, objective and impartial tone without strong emotions",
    },
  ];

  const validateSingleField = (fieldId, value) => {
    const newErrors = { ...errors };

    const urlRegex = /^(https:\/\/)[^\s/$.?#].[^\s]*$/i;
    const localhostRegex =
      /^(http:\/\/localhost|http:\/\/127\.0\.0\.1|http:\/\/\d{1,3}(\.\d{1,3}){3})/;

    const handleUrlValidation = (value, key) => {
      if (/\s/.test(value)) {
        newErrors[key] = "URL cannot contain spaces";
      } else if (localhostRegex.test(value)) {
        newErrors[key] = "Localhost URLs are not allowed";
      } else if (!value.startsWith("https://")) {
        newErrors[key] = "Only HTTPS URLs are allowed";
      } else if (!urlRegex.test(value)) {
        newErrors[key] = "Enter a valid URL starting with https://";
      } else {
        delete newErrors[key];
      }
    };

    if (fieldId === "shopUrl" && value && !formData.noContent) {
      handleUrlValidation(value, "shopUrl");
    }

    if (fieldId === "competitorUrl" && value) {
      handleUrlValidation(value, "competitorUrl");
    }

    if (fieldId === "keywords" && !value.trim()) {
      delete newErrors.keywords;
    }

    if (!value) {
      delete newErrors[fieldId];
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    let isValid = true;

    if (!formData.competitorUrl) {
      newErrors.competitorUrl = "Please enter competitor URL.";
      isValid = false;
    } else {
      isValid =
        validateSingleField("competitorUrl", formData.competitorUrl) && isValid;
    }

    if (!formData.keywords.trim()) {
      newErrors.keywords = "Please add at least one keyword.";
      isValid = false;
    }

    if (formData.ageRanges.length === 0) {
      newErrors.ageRanges = "Select at least one age group";
      isValid = false;
    }

    if (!formData.gender) {
      newErrors.gender = "Please select a gender";
      isValid = false;
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return { isValid, newErrors };
  };

  const detectChanges = (newData) => {
    const changed = JSON.stringify(newData) !== JSON.stringify(initialFormData);
    setHasChanges(changed);
    if (!hasInteracted) {
      setHasInteracted(true);
    }
  };

  const handleInputChange = (field) => (value) => {
    const updatedData = {
      ...formData,
      [field]: value,
      ...(field === "shopUrl" && value.trim() && { noContent: false }),
    };
    setFormData(updatedData);
    detectChanges(updatedData);
    validateSingleField(field, value);
  };

  const handleAgeRangeChange = (value) => (checked) => {
    const updatedRanges = checked
      ? [...formData.ageRanges, value]
      : formData.ageRanges.filter((range) => range !== value);
    const updatedData = { ...formData, ageRanges: updatedRanges };
    setFormData(updatedData);
    detectChanges(updatedData);
    setErrors((prev) => ({ ...prev, ageRanges: "" }));
  };

  const handleGenderChange = (gender) => {
    const updatedData = { ...formData, gender };
    setFormData(updatedData);
    detectChanges(updatedData);
    setErrors((prev) => ({ ...prev, gender: "" }));
  };

  const handleStep1Continue = () => {
    const isValid = validateStep1();
    if (isValid) {
      setStepModal(2);
      setErrors({});
      setInitialFormData(formData);
      setHasChanges(false);
      setHasInteracted(false); // 👈 reset bar visibility
    }
  };

  const handleStep2Back = () => {
    setStepModal(1);
  };

  const handleCreateArticle = async () => {
    setIsLoading(true);
    setErrors({});

    const newErrors = {};

    if (!formData.competitorUrl.trim()) {
      newErrors.competitorUrl = "Competitor URL is required.";
    }

    if (!formData.keywords.trim()) {
      newErrors.keywords = "Please enter at least one keyword.";
    }

    if (formData.ageRanges.length === 0) {
      newErrors.ageRanges = "Select at least one age range.";
    }

    if (!formData.gender) {
      newErrors.gender = "Select an audience gender.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsLoading(false);
      return;
    }

    const payload = {
      shop: shopOrigin,
      shop_url: formData.shopUrl,
      no_website_content: formData.noContent,
      competitor_url: formData.competitorUrl,
      keywords: formData.keywords,
      age_ranges: JSON.stringify(formData.ageRanges),
      gender: formData.gender,
      step1_completed: true,
      initial_steps_completed: true,
    };

    try {
      await axios.post("/api/save-setup/", payload, {
        headers: { "Content-Type": "application/json" },
      });
      setShowAiSetupModal(false);
      setStepModal(1);
      setErrors({});
      setInitialFormData(formData);
      setHasChanges(false);
      setHasInteracted(false);

      // You can navigate if needed
      navigate(`/tone-content?shop=${encodeURIComponent(shopOrigin || "")}`);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        "Failed to save setup data. Please try again.";
      setErrors({ general: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  // const handleModalClose = () => {
  //   setShowAiSetupModal(false);
  //   setErrors({});
  // };

  const customStyles = `
    .tabs-switch-container {
      display: flex;
      flex-direction: column;
      margin-bottom: 20px;
    }
    .tabs-switch-container div {
      display: flex;
      justify-content: space-around;
      width: 100%;
      margin-top:15px
    }
    .tabs-switch-container p {
      cursor: pointer;
      color: ${
        step === 1
          ? "black"
          : step === 2
          ? "black"
          : step === 3
          ? "black"
          : "rgb(206, 206, 206)"
      };
    }
    .Polaris-ProgressBar__Indicator {
      background-color: black !important;
    }

    .Polaris-ButtonGroup {
      margin-left: 0px;
      margin-top: 6px;
      border: 1px solid black;
      width: fit-content;
      padding: 1px;
      border-radius: 10px;
    }
    .Polaris-ButtonGroup__Item .Polaris-Button--pressed {
      background-color: #000 !important;
      color: #fff !important;
      border-color: #000 !important;
    }
    .Polaris-ButtonGroup__Item {
      padding: 2px;
      margin:0px;
    }
    .Polaris-Button--iconOnly{
      display: none;
    }
    .Polaris-Backdrop{
      background-color:rgba(241, 241, 241, 0.70);
    }
    .Polaris-Frame__ContextualSaveBar{
      z-index: 999999;
    }
    @media (max-width:490px){
      .edit-info-div{
        padding-left: 16px;
      }
      .edit-info-div-1{
        padding-left: 7px;
      }
    }      
  `;

  return (
    <>
      <style>{customStyles}</style>
      <Page
        title="BlogHero Setup"
        className="mainCSS"
        stylestyle={{ paddingTop: hasChanges ? "60px" : "0" }}
      >
        {!showInfo && (
          <>
            <div style={{marginBottom: "15px"}} className="edit-info-div">
              <Text variant="bodyLg" color="subdued">
                Set the tone and writing style for your content
              </Text>
              <Text variant="bodyLg" tone="subdued">
                (You can edit this information at any time)
              </Text>
            </div>
          </>
        )}

        <SetupTabs step={step} setStep={setStep} />

        {step === 1 && (
          <>
            {!showToneOptions && !showSampleText && !showUrlText && (
              <>
                <Card padding={{ xs: "500", sm: "800" }}>
                  <Text variant="headingMd" as="h2">
                    Set the tone of your blog
                  </Text>
                  <Box paddingBlockStart="400">
                    <InlineStack gap="400" direction="column">
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                        onClick={() => {
                          toneSelection(); // your existing handler
                          setShowUrlText(false); // hides the error section
                          setError(""); // removes the error card
                          setUrlText(""); // optional: resets input
                          setShowInfo(true);
                        }}
                      >
                        <div
                          style={{
                            width: "100%",
                            padding: "10px",
                            border: "1px solid #ccc",
                            borderRadius: "8px",
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          Choose the style & tone from a list
                          <svg
                            viewBox="0 0 20 20"
                            className="Icon_Icon__uZZKy"
                            style={{ width: "20px", height: "20px" }}
                          >
                            <path
                              fillRule="evenodd"
                              d="M7.72 14.53a.75.75 0 0 1 0-1.06l3.47-3.47-3.47-3.47a.75.75 0 0 1 1.06-1.06l4 4a.75.75 0 0 1 0 1.06l-4 4a.75.75 0 0 1-1.06 0Z"
                            ></path>
                          </svg>
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                        onClick={() => {
                          setShowUrlText(true);
                          setShowInfo(true); // Add this line
                        }}
                      >
                        <div
                          style={{
                            width: "100%",
                            padding: "10px",
                            border: "1px solid #ccc",
                            borderRadius: "8px",
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          Copy the style & tone from a sample link URL
                          <svg
                            viewBox="0 0 20 20"
                            className="Icon_Icon__uZZKy"
                            style={{ width: "20px", height: "20px" }}
                          >
                            <path
                              fillRule="evenodd"
                              d="M7.72 14.53a.75.75 0 0 1 0-1.06l3.47-3.47-3.47-3.47a.75.75 0 0 1 1.06-1.06l4 4a.75.75 0 0 1 0 1.06l-4 4a.75.75 0 0 1-1.06 0Z"
                            ></path>
                          </svg>
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <div
                          style={{
                            width: "100%",
                            padding: "10px",
                            border: "1px solid #ccc",
                            borderRadius: "8px",
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                          onClick={() => {
                            setShowSampleText(true); // your existing action
                            setShowUrlText(false); // hides error/fallback UI
                            setError(""); // removes the error card
                            setUrlText(""); // optional: resets input
                            setShowInfo(true);
                          }}
                        >
                          Paste a Sample text
                          <svg
                            viewBox="0 0 20 20"
                            className="Icon_Icon__uZZKy"
                            style={{ width: "20px", height: "20px" }}
                          >
                            <path
                              fillRule="evenodd"
                              d="M7.72 14.53a.75.75 0 0 1 0-1.06l3.47-3.47-3.47-3.47a.75.75 0 0 1 1.06-1.06l4 4a.75.75 0 0 1 0 1.06l-4 4a.75.75 0 0 1-1.06 0Z"
                            ></path>
                          </svg>
                        </div>
                      </div>
                    </InlineStack>
                  </Box>
                </Card>
                {/* <div
                  style={{
                    marginTop: "20px",
                    marginBottom: "20px",
                    display: "flex",
                    justifyContent: "end",
                    alignItems: "center",
                  }}
                >
                  <Button variant="primary" size="medium">
                    Continue
                  </Button>
                </div> */}
              </>
            )}

            {showToneOptions && (
              <div style={{ marginBottom: "20px" }}>
                <Box>
                  <div style={{marginBottom: "15px"}} className="edit-info-div-1">
                    <Text variant="bodyLg" color="subdued">
                      Set the tone and writing style for your content
                    </Text>
                    <Text variant="bodyLg" tone="subdued">
                      (You can edit this information at any time)
                    </Text>
                  </div>
                  <Card
                    padding={{ xs: "300", sm: "600" }}
                    style={{ marginTop: "20px" }}
                  >
                    {toneOptions.map(({ label, value, description }) => (
                      <div
                        className="tone-style-card"
                        key={value}
                        onClick={() => setSelectedTone(value)}
                        style={{ cursor: "pointer", marginBottom: "12px" }}
                      >
                        <Card>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <RadioButton
                              checked={selectedTone === value}
                              label={
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                  }}
                                >
                                  <Text variant="bodyMd" fontWeight="medium">
                                    {label}
                                  </Text>
                                  <Text variant="bodySm" color="subdued">
                                    {description}
                                  </Text>
                                </div>
                              }
                              id={value}
                              name="toneStyle"
                              onChange={() => setSelectedTone(value)}
                            />
                          </div>
                        </Card>
                      </div>
                    ))}
                  </Card>
                  <div
                    style={{
                      marginTop: "20px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginRight: "20px",
                      marginLeft: "20px",
                      ...mobileStyles
                    }}
                  >
                    <Button onClick={() => setShowToneOptions(false)}>
                      Back
                    </Button>
                    <Button
                      variant="primary"
                      disabled={!selectedTone}
                      onClick={handleContinue}
                    >
                      Continue
                    </Button>
                  </div>
                </Box>
              </div>
            )}

            {/* Paste from url diologue box */}

            {showUrlText && (
              <>
                {error ? (
                  // 👉 THIS IS THE FALLBACK UI after URL fails
                  <>
                    {/* <div style={{marginBottom: "15px"}} className="edit-info-div-1">
                      <Text variant="bodyLg" color="subdued">
                        Set the tone and writing style for your content
                      </Text>
                      <Text variant="bodyLg" tone="subdued">
                        (You can edit this information at any time)
                      </Text>
                    </div> */}
                    <Card padding={{ xs: "500", sm: "800" }}>
                      <div style={{ marginBottom: "16px" }}>
                        <Text
                          tone="critical"
                          variant="bodyMd"
                          fontWeight="medium"
                        >
                          The URL could not be scanned for style
                        </Text>
                        <Text tone="subdued" as="p">
                          The URL you entered couldn’t be analyzed for tone or
                          writing style. Please try one of the methods below.
                        </Text>
                      </div>
                      <Text variant="headingMd" as="h2">
                        Set the tone of your blog
                      </Text>
                      <Box paddingBlockStart="400">
                        <InlineStack gap="400" direction="column">
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              justifyContent: "space-between",
                              width: "100%",
                            }}
                            onClick={() => {
                              toneSelection(); // your existing handler
                              setShowUrlText(false); // hides the error section
                              setError(""); // removes the error card
                              setUrlText(""); // optional: resets input
                            }}
                          >
                            <div
                              style={{
                                width: "100%",
                                padding: "10px",
                                border: "1px solid #ccc",
                                borderRadius: "8px",
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                justifyContent: "space-between",
                              }}
                            >
                              Choose the tone and style from the list
                              <svg
                                viewBox="0 0 20 20"
                                className="Icon_Icon__uZZKy"
                                style={{ width: "20px", height: "20px" }}
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M7.72 14.53a.75.75 0 0 1 0-1.06l3.47-3.47-3.47-3.47a.75.75 0 0 1 1.06-1.06l4 4a.75.75 0 0 1 0 1.06l-4 4a.75.75 0 0 1-1.06 0Z"
                                ></path>
                              </svg>
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              justifyContent: "space-between",
                              width: "100%",
                            }}
                          >
                            <div
                              style={{
                                width: "100%",
                                padding: "10px",
                                border: "1px solid #ccc",
                                borderRadius: "8px",
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                justifyContent: "space-between",
                              }}
                              onClick={() => {
                                setShowSampleText(true); // your existing action
                                setShowUrlText(false); // hides error/fallback UI
                                setError(""); // removes the error card
                                setUrlText(""); // optional: resets input
                              }}
                            >
                              Paste a Sample text
                              <svg
                                viewBox="0 0 20 20"
                                className="Icon_Icon__uZZKy"
                                style={{ width: "20px", height: "20px" }}
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M7.72 14.53a.75.75 0 0 1 0-1.06l3.47-3.47-3.47-3.47a.75.75 0 0 1 1.06-1.06l4 4a.75.75 0 0 1 0 1.06l-4 4a.75.75 0 0 1-1.06 0Z"
                                ></path>
                              </svg>
                            </div>
                          </div>
                        </InlineStack>
                      </Box>
                    </Card>
                    <div
                      style={{
                        marginTop: "24px",
                        display: "flex",
                        justifyContent: "space-between",
                        marginRight: "20px",
                        marginLeft: "20px"
                      }}
                    >
                      <Button
                        onClick={() => {
                          setShowUrlText(false);
                          setUrlText("");
                          setError("");
                        }}
                      >
                        Back
                      </Button>

                      <Button variant="primary" disabled>
                        Continue
                      </Button>
                    </div>
                  </>
                ) : (
                  // 👉 THIS IS THE NORMAL URL input box BEFORE any error
                  <>
                    <div style={{marginBottom: "15px"}} className="edit-info-div-1">
                      <Text variant="bodyLg" color="subdued">
                        Set the tone and writing style for your content
                      </Text>
                      <Text variant="bodyLg" tone="subdued">
                        (You can edit this information at any time)
                      </Text>
                    </div>
                    <Card padding={{ xs: "400", sm: "600" }} sectioned>
                      <Text variant="headingMd" as="h2">
                        Paste the URL of content you like. We'll analyze it and copy its writing style and tone
                      </Text>
                      <Box paddingBlockStart="300">
                        <TextField
                          // label="Paste blog or article URL"
                          value={urlText}
                          onChange={(val) => setUrlText(val)}
                          placeholder="e.g. https://sephora.com/blog/post/beauty-trends"
                          autoComplete="off"
                          error={error}
                        />
                        <Text as="p" tone="subdued">
                          Make sure the page contains publicly readable content. Homepage or image-heavy URLs might not work.
                        </Text>
                      </Box>
                    </Card>
                    <div
                      style={{
                        marginTop: "20px",
                        marginBottom: "20px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Button
                        onClick={() => {
                          setShowUrlText(false);
                          setUrlText("");
                          setError("");
                        }}
                      >
                        Back
                      </Button>
                      <Button
                        variant="primary"
                        disabled={urlText.trim().length === 0}
                        onClick={handleUrlSubmit}
                      >
                        Submit
                      </Button>
                    </div>
                  </>
                )}
              </>
            )}

            {showSampleText && (
              <>
                <div style={{marginBottom: "15px"}} className="edit-info-div-1">
                  <Text variant="bodyLg" color="subdued">
                    Set the tone and writing style for your content
                  </Text>
                  <Text variant="bodyLg" tone="subdued">
                    (You can edit this information at any time)
                  </Text>
                </div>
                <Card padding={{ xs: "400", sm: "600" }} sectioned>
                  <Text variant="headingMd" as="h2">
                    Paste a sample text of content you like
                  </Text>
                  <Text as="p" tone="subdued">
                    We'll analyze it and copy its writing style and tone
                  </Text>
                  <Box paddingBlockStart="300">
                    <TextField
                      // label="Paste a sample blog or article text"
                      value={sampleText}
                      onChange={(val) => setSampleText(val)}
                      multiline={6}
                      maxLength={10000}
                      showCharacterCount
                      placeholder="e.g. One of the most interesting things about organic makeup is that..."
                      autoComplete="off"
                      error={error}
                    />
                  </Box>
                </Card>
                <div
                  style={{
                    marginTop: "20px",
                    marginBottom: "20px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Button
                    onClick={() => {
                      setShowSampleText(false);
                      setError("");
                    }}
                  >
                    Back
                  </Button>
                  <Button
                    variant="primary"
                    disabled={sampleText.trim().length === 0}
                    onClick={handleSampleSubmit}
                  >
                    Submit
                  </Button>
                </div>
              </>
            )}
          </>
        )}

        <>
          {stepModal === 2 && (
            <ContextualSaveBar
              message={
                hasChanges
                  ? "Unsaved changes"
                  : "No changes, but you can SAVE the information or DISCARD."
              }
              saveAction={{
                onAction: () => {
                  if (stepModal === 1) {
                    handleStep1Continue();
                  } else {
                    handleCreateArticle();
                  }
                },
                loading: isLoading,
                disabled: isLoading,
                content: "Save",
              }}
              discardAction={{
                onAction: () => {
                  setShowAiSetupModal(false); // close modal
                  setFormData(initialFormData); // reset data
                  setErrors({});
                  setHasChanges(false);
                  setHasInteracted(false);
                  setStepModal(1);
                  navigate(
                    `/tone-content?shop=${encodeURIComponent(shopOrigin || "")}`
                  );
                },
                content: "Discard",
              }}
              alignContentFlush
            />
          )}
          <Modal
            open={showAiSetupModal}
            title={
              stepModal === 1
                ? "Tell us about your Shop (Step 1 of 2)"
                : "Tell Us about your Competitors (Step 2 of 2)"
            }
            primaryAction={
              stepModal === 1
                ? {
                    content: "Continue",
                    onAction: handleStep1Continue,
                    disabled: isLoading,
                    loading: isLoading,
                  }
                : undefined
            }
            secondaryActions={
              stepModal === 2
                ? [{ content: "Back", onAction: handleStep2Back }]
                : undefined
            }
            footer={
              stepModal === 2 ? null : undefined // 🧼 removes the footer cleanly
            }
          >
            <Modal.Section>
              {errors.general && <Text color="critical">{errors.general}</Text>}
              <FormLayout>
                {stepModal === 1 && (
                  <>
                    <TextField
                      label="Shopify Shop URL"
                      placeholder="e.g., https://www.example.com"
                      value={formData.shopUrl}
                      onChange={handleInputChange("shopUrl")}
                      error={errors.shopUrl}
                      autoComplete="off"
                      disabled={formData.noContent}
                    />
                    <Checkbox
                      label="I don't have any content on my website yet"
                      checked={formData.noContent}
                      onChange={handleInputChange("noContent")}
                      disabled={formData.shopUrl.trim().length > 0}
                    />
                  </>
                )}
                {stepModal === 2 && (
                  <>
                    <TextField
                      label="Competitor website"
                      placeholder="https://www.example.com"
                      value={formData.competitorUrl}
                      onChange={handleInputChange("competitorUrl")}
                      error={errors.competitorUrl}
                      helpText="Helps AI understand tone and style"
                    />
                    <TextField
                      label="Product keywords"
                      placeholder="e.g., lipstick, vegan skincare"
                      value={formData.keywords}
                      onChange={handleInputChange("keywords")}
                      error={errors.keywords}
                      helpText="Comma separated"
                    />
                    <div>
                      <Text variant="bodyMd" fontWeight="medium">
                        Audience Age Range
                      </Text>
                      <InlineStack direction="column">
                        {["16-22", "23-32", "33-45", "46-60", "60+"].map(
                          (range) => (
                            <Checkbox
                              key={range}
                              label={range}
                              checked={formData.ageRanges.includes(range)}
                              onChange={handleAgeRangeChange(range)}
                            />
                          )
                        )}
                        {errors.ageRanges && (
                          <Text color="critical">{errors.ageRanges}</Text>
                        )}
                      </InlineStack>
                    </div>
                    <div>
                      <Text variant="bodyMd" fontWeight="medium">
                        Audience Gender
                      </Text>
                      <ButtonGroup segmented>
                        {["Male", "Female", "All Genders"].map((gender) => (
                          <Button
                            key={gender}
                            pressed={formData.gender === gender}
                            onClick={() => handleGenderChange(gender)}
                          >
                            {gender}
                          </Button>
                        ))}
                      </ButtonGroup>
                      {errors.gender && (
                        <Text color="critical">{errors.gender}</Text>
                      )}
                    </div>
                  </>
                )}
              </FormLayout>
            </Modal.Section>
          </Modal>
        </>
      </Page>
    </>
  );
}

export default ToneContent;
